import { truncateToDecimals } from '@core/i18n';
import {
  decrementStakeInList,
  incrementStakeInList,
} from '@core/gameengine/config/GameBetLimits';

/** Default stop amounts (+/−) before doubling from 5000. */
export const DEFAULT_AUTOPLAY_STOP_BASE_VALUES = [
  0.1, 0.5, 1, 2, 5, 10, 20, 50, 100, 1000, 5000,
] as const;

const DOUBLE_ANCHOR = 5000;

function toAmountCents(value: number): number {
  return Math.round(truncateToDecimals(value) * 100);
}

/** All allowed +/− steps up to `maxValue` (base list + 10k, 20k, … from 5000). */
export function buildAutoplayStopSteps(
  maxValue: number,
  baseValues: readonly number[] = DEFAULT_AUTOPLAY_STOP_BASE_VALUES
): number[] {
  const first = baseValues[0] ?? 0.1;
  const cappedMax = Math.max(maxValue, first);
  const steps: number[] = baseValues.filter(
    value => value <= cappedMax + 0.001
  );

  let doubled = DOUBLE_ANCHOR * 2;
  while (doubled <= cappedMax + 0.001) {
    steps.push(doubled);
    doubled *= 2;
  }

  return [...new Set(steps)].sort((a, b) => a - b);
}

export function incrementAutoplayStopAmount(
  current: number,
  maxValue: number,
  minValue = 0,
  baseValues: readonly number[] = DEFAULT_AUTOPLAY_STOP_BASE_VALUES
): number {
  const steps = buildAutoplayStopSteps(maxValue, baseValues);
  if (!steps.length) {
    return truncateToDecimals(Math.min(maxValue, Math.max(minValue, current)));
  }

  if (toAmountCents(current) <= toAmountCents(minValue)) {
    return truncateToDecimals(Math.min(maxValue, steps[0]));
  }

  const next = incrementStakeInList(current, steps);
  return truncateToDecimals(Math.min(maxValue, next));
}

export function decrementAutoplayStopAmount(
  current: number,
  maxValue: number,
  minValue = 0,
  baseValues: readonly number[] = DEFAULT_AUTOPLAY_STOP_BASE_VALUES
): number {
  const steps = buildAutoplayStopSteps(maxValue, baseValues);
  if (toAmountCents(current) <= toAmountCents(minValue)) {
    return minValue;
  }
  if (steps.length && toAmountCents(current) <= toAmountCents(steps[0])) {
    return minValue;
  }

  const prev = decrementStakeInList(current, steps);
  return truncateToDecimals(Math.max(minValue, prev));
}

export function isAutoplayStopIncreaseDisabled(
  current: number,
  maxValue: number,
  minValue = 0,
  baseValues: readonly number[] = DEFAULT_AUTOPLAY_STOP_BASE_VALUES
): boolean {
  if (toAmountCents(current) >= toAmountCents(maxValue)) {
    return true;
  }
  return (
    incrementAutoplayStopAmount(current, maxValue, minValue, baseValues) ===
    truncateToDecimals(current)
  );
}

export function isAutoplayStopDecreaseDisabled(
  current: number,
  maxValue: number,
  minValue = 0,
  baseValues: readonly number[] = DEFAULT_AUTOPLAY_STOP_BASE_VALUES
): boolean {
  if (toAmountCents(current) <= toAmountCents(minValue)) {
    return true;
  }
  return (
    decrementAutoplayStopAmount(current, maxValue, minValue, baseValues) ===
    truncateToDecimals(current)
  );
}

/** Display stop amounts with 2 decimal places at every step (including ≥ 100). */
export function formatAutoplayStopInputDisplay(
  value: number,
  formatEditableAmount: (amount: number) => string
): string {
  return formatEditableAmount(truncateToDecimals(value));
}

/** Clamp a typed stop amount to [minValue, maxValue] without snapping to +/- steps. */
export function clampAutoplayStopAmount(
  amount: number,
  maxValue: number,
  minValue = 0
): number {
  return truncateToDecimals(Math.min(maxValue, Math.max(minValue, amount)));
}
